/**
 * AdminDashboard component
 *
 * Lists all uploaded tracks with metadata and delete controls.
 * Contains the upload form for new tracks.
 * 
 * Design: Warm dark palette with amber accents, refined typography.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { UploadZone } from './UploadZone';
import { AISettings } from '../Admin/AISettings';
import { TrackSummary, Difficulty } from '../../types';
import api from '../../services/authAPI';
import { API_BASE_URL } from '../../config';

interface UploadFormState {
  title: string;
  artist: string;
  difficulty: Difficulty;
  minNoteGapMs: number;
  maxNotesPerSecond: number;
  onsetThreshold: number;
}

const DIFFICULTY_LABELS: Record<Difficulty, string> = {
  easy:   'Beginner',
  medium: 'Intermediate',
  hard:   'Advanced',
  expert: 'Expert',
};

const DIFFICULTY_COLORS: Record<Difficulty, string> = {
  easy:   'text-sage-400 bg-sage-400/10 border-sage-400/30',
  medium: 'text-amber-400 bg-amber-400/10 border-amber-400/30',
  hard:   'text-coral-400 bg-coral-400/10 border-coral-400/30',
  expert: 'text-stone-300 bg-stone-400/10 border-stone-400/30',
};

export const AdminDashboard: React.FC = () => {
  const [tracks, setTracks] = useState<TrackSummary[]>([]);
  const [loadingTracks, setLoadingTracks] = useState(true);
  const [tracksError, setTracksError] = useState<string | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [form, setForm] = useState<UploadFormState>({
    title: '',
    artist: '',
    difficulty: 'medium',
    minNoteGapMs: 200,
    maxNotesPerSecond: 8,
    onsetThreshold: 2.2,
  });
  const [showInstructions, setShowInstructions] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Load tracks
  // -------------------------------------------------------------------------

  const fetchTracks = useCallback(async () => {
    setLoadingTracks(true);
    setTracksError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/tracks`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as TrackSummary[];
      setTracks(data);
    } catch (e) {
      setTracksError((e as Error).message);
    } finally {
      setLoadingTracks(false);
    }
  }, []);

  useEffect(() => {
    void fetchTracks();
  }, [fetchTracks]);

  // -------------------------------------------------------------------------
  // Delete track
  // -------------------------------------------------------------------------

  const deleteTrack = useCallback(
    async (id: string) => {
      if (!confirm('Delete this track? This cannot be undone.')) return;
      try {
        await api.delete(`/tracks/${id}`);
        setTracks((prev) => prev.filter((t) => t.id !== id));
      } catch (e) {
        alert(`Delete failed: ${(e as Error).message}`);
      }
    },
    []
  );

  // -------------------------------------------------------------------------
  // Upload
  // -------------------------------------------------------------------------

  const handleFileSelected = useCallback((file: File) => {
    setSelectedFile(file);
    setForm((prev) => ({
      ...prev,
      title: prev.title || file.name.replace(/\.[^.]+$/, '').replace(/_/g, ' '),
    }));
    setUploadError(null);
    setUploadSuccess(null);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!selectedFile) {
        setUploadError('Please select an audio file first.');
        return;
      }
      if (!form.title.trim()) {
        setUploadError('Track title is required.');
        return;
      }

      setIsUploading(true);
      setUploadProgress(10);
      setUploadError(null);
      setUploadSuccess(null);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', form.title.trim());
      formData.append('artist', form.artist.trim());
      formData.append('difficulty', form.difficulty);
      formData.append('minNoteGapMs', form.minNoteGapMs.toString());
      formData.append('maxNotesPerSecond', form.maxNotesPerSecond.toString());
      formData.append('onsetThreshold', form.onsetThreshold.toString());

      try {
        const progressInterval = setInterval(() => {
          setUploadProgress((p) => Math.min(p + 5, 90));
        }, 500);

        const res = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        clearInterval(progressInterval);
        setUploadProgress(100);

        if (res.status !== 201) {
          throw new Error(`Upload failed (${res.status})`);
        }

        setUploadSuccess('Track uploaded and processed successfully!');
        setSelectedFile(null);
        setForm({
          title: '',
          artist: '',
          difficulty: 'medium',
          minNoteGapMs: 200,
          maxNotesPerSecond: 8,
          onsetThreshold: 2.2,
        });
        void fetchTracks();
      } catch (e) {
        setUploadError((e as Error).message);
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    },
    [selectedFile, form, fetchTracks]
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <h1 className="font-display text-4xl text-stone-100 mb-2">
            Studio
          </h1>
          <p className="text-stone-500">
            Upload and manage practice exercises
          </p>
        </div>

        {/* AI Settings Section */}
        <div className="mb-10">
          <AISettings />
        </div>

        {/* Upload Section */}
        <div className="card-editorial rounded-2xl p-8 mb-10">
          <h2 className="text-xl font-semibold text-stone-100 mb-6">
            Upload New Exercise
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <UploadZone
              onFileSelected={handleFileSelected}
              isUploading={isUploading}
              uploadProgress={uploadProgress}
            />

            {selectedFile && (
              <div className="flex items-center gap-3 px-4 py-3 bg-studio-800 rounded-lg">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0 border border-amber-500/30">
                  <span className="text-amber-500 text-xs font-bold">
                    {selectedFile.name.split('.').pop()?.toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-stone-200 text-sm font-medium truncate">
                    {selectedFile.name}
                  </p>
                  <p className="text-stone-500 text-xs">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedFile(null)}
                  className="text-stone-500 hover:text-coral-400 transition-colors p-1"
                  aria-label="Remove file"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            )}

            {/* Metadata fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-stone-400 mb-2">
                  Title <span className="text-coral-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, title: e.target.value }))
                  }
                  placeholder="e.g. Quarter Note Patterns"
                  className="w-full bg-studio-900 border border-studio-700 rounded-lg px-4 py-2.5
                             text-stone-200 placeholder-stone-600
                             focus:outline-none focus:border-amber-500/50 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm text-stone-400 mb-2">
                  Artist / Composer
                </label>
                <input
                  type="text"
                  value={form.artist}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, artist: e.target.value }))
                  }
                  placeholder="e.g. RhythmSense Library"
                  className="w-full bg-studio-900 border border-studio-700 rounded-lg px-4 py-2.5
                             text-stone-200 placeholder-stone-600
                             focus:outline-none focus:border-amber-500/50 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-stone-400 mb-3">
                Difficulty Level
              </label>
              <div className="flex gap-2">
                {(['easy', 'medium', 'hard', 'expert'] as Difficulty[]).map(
                  (diff) => (
                    <button
                      key={diff}
                      type="button"
                      onClick={() => {
                        // Apply presets when clicking difficulty
                        const presets: Record<Difficulty, { gap: number; density: number; threshold: number }> = {
                          easy: { gap: 300, density: 3, threshold: 2.5 },
                          medium: { gap: 200, density: 6, threshold: 2.0 },
                          hard: { gap: 120, density: 10, threshold: 1.5 },
                          expert: { gap: 80, density: 15, threshold: 1.2 },
                        };
                        const preset = presets[diff];
                        setForm((p) => ({
                          ...p,
                          difficulty: diff,
                          minNoteGapMs: preset.gap,
                          maxNotesPerSecond: preset.density,
                          onsetThreshold: preset.threshold,
                        }));
                      }}
                      className={[
                        'px-4 py-2 rounded-lg text-sm font-medium transition-all border',
                        form.difficulty === diff
                          ? DIFFICULTY_COLORS[diff]
                          : 'bg-studio-800 text-stone-500 border-studio-700 hover:border-studio-600',
                      ].join(' ')}
                    >
                      {DIFFICULTY_LABELS[diff]}
                    </button>
                  )
                )}
              </div>
            </div>

            {/* Advanced Difficulty Parameters */}
            <div className="border border-studio-800 rounded-xl p-5 bg-studio-900/50">
              <h3 className="text-sm font-semibold text-stone-300 mb-4 flex items-center gap-2">
                <svg className="w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                Advanced Parameters
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {/* Min Note Gap */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-stone-400">Min Note Gap</label>
                    <span className="text-xs font-mono text-amber-500">{form.minNoteGapMs}ms</span>
                  </div>
                  <input
                    type="range"
                    min="50"
                    max="500"
                    step="10"
                    value={form.minNoteGapMs}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, minNoteGapMs: parseInt(e.target.value) }))
                    }
                    className="w-full h-2 bg-studio-800 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                  <div className="flex justify-between text-[10px] text-stone-600 mt-1">
                    <span>Hard (50ms)</span>
                    <span>Easy (500ms)</span>
                  </div>
                </div>

                {/* Max Notes Per Second */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-stone-400">Max Density</label>
                    <span className="text-xs font-mono text-amber-500">{form.maxNotesPerSecond} notes/s</span>
                  </div>
                  <input
                    type="range"
                    min="2"
                    max="15"
                    step="1"
                    value={form.maxNotesPerSecond}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, maxNotesPerSecond: parseInt(e.target.value) }))
                    }
                    className="w-full h-2 bg-studio-800 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                  <div className="flex justify-between text-[10px] text-stone-600 mt-1">
                    <span>Easy (2)</span>
                    <span>Insane (15)</span>
                  </div>
                </div>

                {/* Onset Threshold */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-stone-400">Detection Sensitivity</label>
                    <span className="text-xs font-mono text-amber-500">{form.onsetThreshold.toFixed(1)}</span>
                  </div>
                  <input
                    type="range"
                    min="1.0"
                    max="3.0"
                    step="0.1"
                    value={form.onsetThreshold}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, onsetThreshold: parseFloat(e.target.value) }))
                    }
                    className="w-full h-2 bg-studio-800 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                  <div className="flex justify-between text-[10px] text-stone-600 mt-1">
                    <span>More notes (1.0)</span>
                    <span>Less notes (3.0)</span>
                  </div>
                </div>
              </div>

              {/* Parameter Instructions Accordion */}
              <div className="mt-5 pt-4 border-t border-studio-800">
                <button
                  type="button"
                  onClick={() => setShowInstructions(!showInstructions)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <h4 className="text-xs font-semibold text-stone-400">Parameter Guide</h4>
                  <svg
                    className={`w-4 h-4 text-stone-500 transition-transform ${showInstructions ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showInstructions && (
                  <ul className="mt-3 text-xs text-stone-500 space-y-1 list-disc list-inside">
                    <li><span className="text-stone-400">Min Note Gap:</span> 50-500ms (lower = harder)</li>
                    <li><span className="text-stone-400">Max Density:</span> 2-15 notes/second (higher = harder)</li>
                    <li><span className="text-stone-400">Detection Sensitivity:</span> 1.0-3.0 (lower = more notes detected)</li>
                  </ul>
                )}
              </div>

              {/* Difficulty Presets Accordion */}
              <div className="mt-4 pt-4 border-t border-studio-800">
                <button
                  type="button"
                  onClick={() => setShowPresets(!showPresets)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <div>
                    <h4 className="text-xs font-semibold text-stone-400">5. Difficulty Presets</h4>
                    {!showPresets && <p className="text-[10px] text-stone-600 mt-0.5">Click to view preset values</p>}
                  </div>
                  <svg
                    className={`w-4 h-4 text-stone-500 transition-transform ${showPresets ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showPresets && (
                  <>
                    <p className="mt-3 text-xs text-stone-500">Clicking difficulty buttons auto-sets parameters:</p>
                    <div className="mt-3 overflow-hidden rounded-lg border border-studio-800">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-studio-800/50">
                            <th className="px-3 py-2 text-left text-stone-400 font-medium">Difficulty</th>
                            <th className="px-3 py-2 text-right text-stone-400 font-medium">Gap</th>
                            <th className="px-3 py-2 text-right text-stone-400 font-medium">Density</th>
                            <th className="px-3 py-2 text-right text-stone-400 font-medium">Threshold</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-studio-800">
                          <tr>
                            <td className="px-3 py-2 text-sage-400 font-medium">Easy</td>
                            <td className="px-3 py-2 text-right text-stone-300">300ms</td>
                            <td className="px-3 py-2 text-right text-stone-300">3</td>
                            <td className="px-3 py-2 text-right text-stone-300">2.5</td>
                          </tr>
                          <tr>
                            <td className="px-3 py-2 text-amber-400 font-medium">Medium</td>
                            <td className="px-3 py-2 text-right text-stone-300">200ms</td>
                            <td className="px-3 py-2 text-right text-stone-300">6</td>
                            <td className="px-3 py-2 text-right text-stone-300">2.0</td>
                          </tr>
                          <tr>
                            <td className="px-3 py-2 text-coral-400 font-medium">Hard</td>
                            <td className="px-3 py-2 text-right text-stone-300">120ms</td>
                            <td className="px-3 py-2 text-right text-stone-300">10</td>
                            <td className="px-3 py-2 text-right text-stone-300">1.5</td>
                          </tr>
                          <tr>
                            <td className="px-3 py-2 text-stone-300 font-medium">Expert</td>
                            <td className="px-3 py-2 text-right text-stone-300">80ms</td>
                            <td className="px-3 py-2 text-right text-stone-300">15</td>
                            <td className="px-3 py-2 text-right text-stone-300">1.2</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>

            {uploadError && (
              <div className="px-4 py-3 bg-coral-400/10 border border-coral-400/30 rounded-lg text-coral-400 text-sm">
                {uploadError}
              </div>
            )}
            {uploadSuccess && (
              <div className="px-4 py-3 bg-sage-400/10 border border-sage-400/30 rounded-lg text-sage-400 text-sm">
                {uploadSuccess}
              </div>
            )}

            <button
              type="submit"
              disabled={isUploading || !selectedFile}
              className="w-full py-3 px-6 rounded-xl font-semibold
                         bg-amber-500 text-studio-950 hover:bg-amber-400
                         disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              {isUploading ? 'Processing...' : 'Upload & Process'}
            </button>
          </form>
        </div>

        {/* Track List */}
        <div className="card-editorial rounded-2xl p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-stone-100">
              Exercise Library
            </h2>
            <button
              type="button"
              onClick={() => void fetchTracks()}
              className="text-sm text-amber-500 hover:text-amber-400 transition-colors"
            >
              Refresh
            </button>
          </div>

          {loadingTracks ? (
            <div className="flex justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-stone-500 text-sm">Loading exercises...</p>
              </div>
            </div>
          ) : tracksError ? (
            <p className="text-coral-400 text-center py-12">{tracksError}</p>
          ) : tracks.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-studio-800 rounded-xl">
              <p className="text-stone-500">
                No exercises uploaded yet.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {tracks.map((track) => (
                <TrackRow
                  key={track.id}
                  track={track}
                  onDelete={deleteTrack}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TrackRow sub-component
// ---------------------------------------------------------------------------

interface TrackRowProps {
  track: TrackSummary;
  onDelete: (id: string) => void;
}

const TrackRow: React.FC<TrackRowProps> = ({ track, onDelete }) => {
  const diff = (track.difficulty ?? 'medium') as Difficulty;

  return (
    <div className="flex items-center justify-between px-4 py-4 bg-studio-800/50
                    rounded-xl border border-studio-700 hover:border-studio-600 transition-colors">
      <div className="flex-1 min-w-0">
        <p className="text-stone-200 font-medium truncate">{track.title}</p>
        <p className="text-stone-500 text-sm">
          {track.artist ?? 'Unknown Artist'}
          {track.bpm ? ` · ${Math.round(track.bpm)} BPM` : ''}
          {track.duration
            ? ` · ${Math.floor(track.duration / 60)}:${String(
                Math.floor(track.duration % 60)
              ).padStart(2, '0')}`
            : ''}
        </p>
      </div>

      <div className="flex items-center gap-3 ml-4">
        <span
          className={[
            'px-2.5 py-1 rounded-lg text-xs font-medium border',
            DIFFICULTY_COLORS[diff] ?? '',
          ].join(' ')}
        >
          {DIFFICULTY_LABELS[diff] ?? diff}
        </span>

        <button
          type="button"
          onClick={() => onDelete(track.id)}
          className="p-2 text-stone-500 hover:text-coral-400 transition-colors rounded-lg
                     hover:bg-coral-400/10"
          aria-label={`Delete ${track.title}`}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
        </button>
      </div>
    </div>
  );
};
